/** 2-D overlay: pixel grid & values, contours, ROIs, handles, labels, scale bar and legend. */
import { colormapLut, type ColormapId } from '../core/colormaps';
import type { ContourLevel } from '../core/contours';
import { normalizeRect, type Roi } from '../core/roi';
import type { Plane, Vec2 } from '../core/types';
import { fmt, type SpatialCalibration } from '../core/units';
import { handlesOf } from './handles';
import { toImage, toScreen, type View } from './transform';

export interface LegendSpec {
  mode: 'gray' | 'false';
  colormap: ColormapId;
  bands: number;
  lo: number;
  hi: number;
  log: boolean;
  label: string;
}

export interface LineMarks {
  roiId: string;
  /** Distances along the line (px) to mark, e.g. peaks and FWHM edges. */
  ticks: { t: number; kind: 'peak' | 'edge' }[];
}

export interface OverlayState {
  view: View;
  cssW: number;
  cssH: number;
  dpr: number;
  imageW: number;
  imageH: number;
  rois: Roi[];
  selectedId: string | null;
  draft: Roi | null;
  polygonDraft: { pts: Vec2[]; cursor: Vec2 | null } | null;
  plane: Plane | null;
  showGrid: boolean;
  showValues: boolean;
  showLabels: boolean;
  showScaleBar: boolean;
  spatial: SpatialCalibration;
  contours: { levels: ContourLevel[]; color: string; labels: boolean } | null;
  plotMarker: Vec2 | null;
  lineMarks: LineMarks | null;
  legend: LegendSpec | null;
  live: boolean;
}

const pathCache = new WeakMap<Float32Array, Path2D>();

function segPath(seg: Float32Array): Path2D {
  let p = pathCache.get(seg);
  if (p) return p;
  p = new Path2D();
  for (let i = 0; i < seg.length; i += 4) {
    p.moveTo(seg[i], seg[i + 1]);
    p.lineTo(seg[i + 2], seg[i + 3]);
  }
  pathCache.set(seg, p);
  return p;
}

function label(c: CanvasRenderingContext2D, text: string, x: number, y: number, color: string) {
  c.font = '600 11px Inter, system-ui, sans-serif';
  const w = c.measureText(text).width + 8;
  c.fillStyle = 'rgba(12,14,18,0.78)';
  c.fillRect(x, y - 8, w, 16);
  c.fillStyle = color;
  c.textBaseline = 'middle';
  c.fillText(text, x + 4, y);
}

function niceStep(v: number): number {
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v / p;
  return (m >= 5 ? 5 : m >= 2 ? 2 : 1) * p;
}

export function drawOverlay(c: CanvasRenderingContext2D, s: OverlayState) {
  const { view, dpr } = s;
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, s.cssW, s.cssH);
  if (!s.imageW) return;

  // Visible image rectangle (pixel indices).
  const [ix0, iy0] = toImage(view, 0, 0);
  const [ix1, iy1] = toImage(view, s.cssW, s.cssH);
  const vx0 = Math.max(0, Math.floor(ix0));
  const vy0 = Math.max(0, Math.floor(iy0));
  const vx1 = Math.min(s.imageW, Math.ceil(ix1));
  const vy1 = Math.min(s.imageH, Math.ceil(iy1));

  if (s.showGrid && view.scale >= 8) {
    c.beginPath();
    for (let x = vx0; x <= vx1; x++) {
      const sx = Math.round(x * view.scale + view.tx) + 0.5;
      c.moveTo(sx, Math.max(0, view.ty + vy0 * view.scale));
      c.lineTo(sx, Math.min(s.cssH, view.ty + vy1 * view.scale));
    }
    for (let y = vy0; y <= vy1; y++) {
      const sy = Math.round(y * view.scale + view.ty) + 0.5;
      c.moveTo(Math.max(0, view.tx + vx0 * view.scale), sy);
      c.lineTo(Math.min(s.cssW, view.tx + vx1 * view.scale), sy);
    }
    c.strokeStyle = `rgba(128,128,128,${Math.min(0.55, (view.scale - 6) / 30)})`;
    c.lineWidth = 1;
    c.stroke();
  }

  if (s.showValues && s.plane && view.scale >= 44 && (vx1 - vx0) * (vy1 - vy0) <= 4000) {
    const p = s.plane;
    const fs = Math.min(13, Math.max(9, view.scale / 6));
    c.font = `500 ${fs}px "JetBrains Mono", ui-monospace, monospace`;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.lineWidth = 3;
    c.strokeStyle = 'rgba(0,0,0,0.75)';
    c.fillStyle = '#fff';
    for (let y = vy0; y < vy1; y++)
      for (let x = vx0; x < vx1; x++) {
        const lx = x - p.ox;
        const ly = y - p.oy;
        if (lx < 0 || ly < 0 || lx >= p.width || ly >= p.height) continue;
        const t = fmt(p.data[ly * p.width + lx], 4);
        const [sx, sy] = toScreen(view, x + 0.5, y + 0.5);
        c.strokeText(t, sx, sy);
        c.fillText(t, sx, sy);
      }
    c.textAlign = 'left';
  }

  if (s.contours && !s.live) {
    c.save();
    c.setTransform(dpr * view.scale, 0, 0, dpr * view.scale, dpr * view.tx, dpr * view.ty);
    c.lineWidth = 1.25 / view.scale;
    c.strokeStyle = s.contours.color;
    for (const lv of s.contours.levels) c.stroke(segPath(lv.segments));
    c.restore();
    if (s.contours.labels) {
      // Place up to two labels per level, skipping positions that would overlap earlier labels.
      c.font = '600 11px Inter, system-ui, sans-serif';
      const placed: [number, number, number, number][] = [];
      const free = (x: number, y: number, w: number) =>
        placed.every(([a, b, cw]) => x + w < a || x > a + cw || y + 9 < b - 9 || y - 9 > b + 9);
      for (const lv of s.contours.levels) {
        const seg = lv.segments;
        const nSeg = seg.length / 4;
        if (!nSeg) continue;
        const text = fmt(lv.level, 3);
        const w = c.measureText(text).width + 8;
        const stride = Math.max(1, Math.floor(nSeg / 48));
        let count = 0;
        for (let j = 0; j < nSeg && count < 2; j += stride) {
          // Visit candidates alternately from the start and the end of the list.
          const k = j % 2 ? nSeg - 1 - Math.floor(j / 2) : Math.floor(j / 2);
          const [sx, sy] = toScreen(view, seg[k * 4], seg[k * 4 + 1]);
          if (sx < 0 || sy < 10 || sx + w > s.cssW || sy > s.cssH - 10) continue;
          if (!free(sx - w / 2, sy, w)) continue;
          placed.push([sx - w / 2, sy, w, 18]);
          label(c, text, sx - w / 2, sy, s.contours.color);
          count++;
        }
      }
    }
  }

  const all = s.draft ? [...s.rois, s.draft] : s.rois;
  for (const r of all) if (r.visible) drawRoi(c, r, view, r.id === s.selectedId || r === s.draft, s.showLabels);

  if (s.polygonDraft && s.polygonDraft.pts.length) {
    const pts = [...s.polygonDraft.pts, ...(s.polygonDraft.cursor ? [s.polygonDraft.cursor] : [])];
    c.beginPath();
    pts.forEach((p, i) => {
      const [x, y] = toScreen(view, p.x, p.y);
      if (i) c.lineTo(x, y);
      else c.moveTo(x, y);
    });
    c.setLineDash([5, 4]);
    c.strokeStyle = '#fff';
    c.lineWidth = 1.5;
    c.stroke();
    c.setLineDash([]);
    const [fx, fy] = toScreen(view, pts[0].x, pts[0].y);
    c.strokeRect(fx - 5, fy - 5, 10, 10);
  }

  if (s.lineMarks) {
    const r = s.rois.find((x) => x.id === s.lineMarks!.roiId);
    if (r && r.kind === 'line' && r.visible) {
      const L = Math.hypot(r.x1 - r.x0, r.y1 - r.y0) || 1;
      const ux = (r.x1 - r.x0) / L;
      const uy = (r.y1 - r.y0) / L;
      const [nx, ny] = [-uy, ux];
      for (const m of s.lineMarks.ticks) {
        const [sx, sy] = toScreen(view, r.x0 + ux * m.t, r.y0 + uy * m.t);
        const len = m.kind === 'peak' ? 9 : 6;
        c.beginPath();
        c.moveTo(sx - nx * len, sy - ny * len);
        c.lineTo(sx + nx * len, sy + ny * len);
        c.strokeStyle = m.kind === 'peak' ? '#ffffff' : '#ffd166';
        c.lineWidth = 2;
        c.stroke();
      }
    }
  }

  if (s.plotMarker) {
    const [sx, sy] = toScreen(view, s.plotMarker.x, s.plotMarker.y);
    c.beginPath();
    c.arc(sx, sy, 6, 0, Math.PI * 2);
    c.strokeStyle = '#000';
    c.lineWidth = 4;
    c.stroke();
    c.strokeStyle = '#fff';
    c.lineWidth = 2;
    c.stroke();
  }

  if (s.showScaleBar) drawScaleBar(c, s);
  if (s.legend) drawLegend(c, s.legend, s.cssW, s.cssH);
}

function drawRoi(c: CanvasRenderingContext2D, r: Roi, view: View, selected: boolean, showLabel: boolean) {
  const P = (x: number, y: number) => toScreen(view, x, y);
  c.lineWidth = selected ? 2 : 1.5;
  c.strokeStyle = r.color;
  c.fillStyle = r.color;
  let labelAt: [number, number] = [0, 0];
  const shadow = () => {
    c.save();
    c.strokeStyle = 'rgba(0,0,0,0.6)';
    c.lineWidth = c.lineWidth + 2;
    c.stroke();
    c.restore();
    c.stroke();
  };
  switch (r.kind) {
    case 'line': {
      const [ax, ay] = P(r.x0, r.y0);
      const [bx, by] = P(r.x1, r.y1);
      const L = Math.hypot(bx - ax, by - ay) || 1;
      const ux = (bx - ax) / L;
      const uy = (by - ay) / L;
      if (r.width > 1) {
        const h = (r.width / 2) * view.scale;
        c.beginPath();
        c.moveTo(ax - uy * h, ay + ux * h);
        c.lineTo(bx - uy * h, by + ux * h);
        c.lineTo(bx + uy * h, by - ux * h);
        c.lineTo(ax + uy * h, ay - ux * h);
        c.closePath();
        c.save();
        c.globalAlpha = 0.12;
        c.fill();
        c.restore();
        c.setLineDash([4, 3]);
        c.lineWidth = 1;
        c.stroke();
        c.setLineDash([]);
        c.lineWidth = selected ? 2 : 1.5;
      }
      c.beginPath();
      c.moveTo(ax, ay);
      c.lineTo(bx, by);
      shadow();
      // Start marker (circle) and direction arrow at the end.
      c.beginPath();
      c.arc(ax, ay, 3.5, 0, Math.PI * 2);
      c.fill();
      c.beginPath();
      c.moveTo(bx, by);
      c.lineTo(bx - ux * 10 - uy * 5, by - uy * 10 + ux * 5);
      c.lineTo(bx - ux * 10 + uy * 5, by - uy * 10 - ux * 5);
      c.closePath();
      c.fill();
      labelAt = [ax + 8, ay - 12];
      break;
    }
    case 'rect': {
      const b = normalizeRect(r);
      const [x0, y0] = P(b.x, b.y);
      c.beginPath();
      c.rect(x0, y0, b.w * view.scale, b.h * view.scale);
      shadow();
      labelAt = [x0, y0 - 12];
      break;
    }
    case 'ellipse': {
      const [cx, cy] = P(r.cx, r.cy);
      c.beginPath();
      c.ellipse(cx, cy, Math.abs(r.rx) * view.scale, Math.abs(r.ry) * view.scale, 0, 0, Math.PI * 2);
      shadow();
      labelAt = [cx - Math.abs(r.rx) * view.scale, cy - Math.abs(r.ry) * view.scale - 12];
      break;
    }
    case 'polygon': {
      c.beginPath();
      r.pts.forEach((p, i) => {
        const [x, y] = P(p.x, p.y);
        if (i) c.lineTo(x, y);
        else c.moveTo(x, y);
      });
      c.closePath();
      shadow();
      const [x, y] = P(r.pts[0]?.x ?? 0, r.pts[0]?.y ?? 0);
      labelAt = [x + 6, y - 12];
      break;
    }
    case 'point': {
      const [x, y] = P(r.x, r.y);
      const h = Math.max(4, (r.size * view.scale) / 2);
      c.beginPath();
      c.moveTo(x - h - 6, y);
      c.lineTo(x - h + 2, y);
      c.moveTo(x + h - 2, y);
      c.lineTo(x + h + 6, y);
      c.moveTo(x, y - h - 6);
      c.lineTo(x, y - h + 2);
      c.moveTo(x, y + h - 2);
      c.lineTo(x, y + h + 6);
      shadow();
      if (r.size * view.scale >= 6) {
        c.beginPath();
        c.rect(x - h, y - h, 2 * h, 2 * h);
        c.stroke();
      }
      labelAt = [x + h + 6, y - h - 8];
      break;
    }
  }
  if (showLabel) label(c, r.name + (r.locked ? ' 🔒' : ''), labelAt[0], labelAt[1], r.color);
  if (selected && !r.locked) {
    for (const h of handlesOf(r, view)) {
      const [x, y] = P(h.x, h.y);
      c.fillStyle = '#fff';
      c.strokeStyle = '#111';
      c.lineWidth = 1;
      if (h.id.startsWith('w')) {
        c.beginPath();
        c.arc(x, y, 4.5, 0, Math.PI * 2);
        c.fill();
        c.stroke();
      } else {
        c.fillRect(x - 4.5, y - 4.5, 9, 9);
        c.strokeRect(x - 4.5, y - 4.5, 9, 9);
      }
    }
  }
}

function drawScaleBar(c: CanvasRenderingContext2D, s: OverlayState) {
  const per = s.spatial.unit === 'px' ? 1 : s.spatial.perPixel;
  const unit = s.spatial.unit;
  const targetPx = 120;
  const len = niceStep((targetPx / s.view.scale) * per);
  const w = (len / per) * s.view.scale;
  const x = 14;
  const y = s.cssH - 18;
  c.fillStyle = 'rgba(12,14,18,0.7)';
  c.fillRect(x - 6, y - 20, w + 12, 30);
  c.fillStyle = '#fff';
  c.fillRect(x, y, w, 3);
  c.fillRect(x, y - 4, 1.5, 7);
  c.fillRect(x + w - 1.5, y - 4, 1.5, 7);
  c.font = '600 11px Inter, system-ui, sans-serif';
  c.textBaseline = 'alphabetic';
  c.fillText(`${fmt(len, 3)} ${unit}`, x, y - 6);
}

function drawLegend(c: CanvasRenderingContext2D, l: LegendSpec, W: number, H: number) {
  const bw = 180;
  const bh = 10;
  const x = W - bw - 20;
  const y = H - 30;
  c.fillStyle = 'rgba(12,14,18,0.75)';
  c.fillRect(x - 10, y - 26, bw + 20, 50);
  const lut = l.mode === 'gray' ? colormapLut('gray') : colormapLut(l.colormap, l.bands);
  for (let i = 0; i < bw; i++) {
    const k = Math.round((i / (bw - 1)) * 255) * 3;
    c.fillStyle = `rgb(${lut[k]},${lut[k + 1]},${lut[k + 2]})`;
    c.fillRect(x + i, y, 1, bh);
  }
  c.strokeStyle = 'rgba(255,255,255,0.4)';
  c.lineWidth = 1;
  c.strokeRect(x + 0.5, y + 0.5, bw - 1, bh - 1);
  c.fillStyle = '#e8eaed';
  c.font = '500 10px Inter, system-ui, sans-serif';
  c.textBaseline = 'alphabetic';
  c.textAlign = 'left';
  c.fillText(fmt(l.lo, 4), x, y + bh + 11);
  c.textAlign = 'right';
  c.fillText(fmt(l.hi, 4), x + bw, y + bh + 11);
  c.textAlign = 'center';
  c.fillText(`${l.label}${l.log ? ' (log)' : ''}`, x + bw / 2, y - 8);
  c.textAlign = 'left';
}
